import {getNumCeilMipLevels, type ImageTilesDescriptor, type TileFetchParams, TiledImageIndex} from "@cm/utils"
import {Size2, type Size2Like, type Vector2Like} from "@cm/math"

export class TileFetchParamsHelper {
    constructor(
        readonly imageTilesDescriptor: ImageTilesDescriptor,
        readonly tileSize: Size2Like,
        readonly tileMargin: number,
    ) {
        const remoteTileSize = {width: imageTilesDescriptor.tileWidth, height: imageTilesDescriptor.tileHeight}
        this._atlasTilesPerRemoteTile = {
            x: remoteTileSize.width / tileSize.width,
            y: remoteTileSize.height / tileSize.height,
        }
        if (!Number.isInteger(this._atlasTilesPerRemoteTile.x) || !Number.isInteger(this._atlasTilesPerRemoteTile.y)) {
            throw new Error(
                `TileFetchParamsHelper: non-integer scale factor between atlas tile size (${tileSize.width}x${tileSize.height}) and remote tile size (${remoteTileSize.width}x${remoteTileSize.height})`,
            )
        }
        this._numTiles = {
            x: Math.ceil(imageTilesDescriptor.imageWidth / tileSize.width),
            y: Math.ceil(imageTilesDescriptor.imageHeight / tileSize.height),
        }
        this._numTileMipLevels = getNumCeilMipLevels(Size2.fromVector2Like(this._numTiles))
        this._tileIndex = TiledImageIndex.fromDescriptor(imageTilesDescriptor)
    }

    get numTileMipLevels(): number {
        return this._numTileMipLevels
    }

    get numTiles(): Vector2Like {
        return this._numTiles
    }

    getTileFetchParams(mipLevel: number, tileIndex: Vector2Like): TileFetchParams {
        const remoteMipLevel = Math.min(mipLevel, this._tileIndex.maxMipLevel)
        const remoteMipLevelCorrectionScale = 1 << (mipLevel - remoteMipLevel)
        const remoteTileIndex = {
            x: Math.floor(tileIndex.x / (this._atlasTilesPerRemoteTile.x * remoteMipLevelCorrectionScale)),
            y: Math.floor(tileIndex.y / (this._atlasTilesPerRemoteTile.y * remoteMipLevelCorrectionScale)),
        }
        const url = this.imageTilesDescriptor.tileDownloadUrlPrefix + this._tileIndex.getTileFilename({mipLevel: remoteMipLevel, tileIndex: remoteTileIndex})
        const remoteTileSize = {
            width: this.imageTilesDescriptor.tileWidth,
            height: this.imageTilesDescriptor.tileHeight,
        }
        const remoteTileMargin = this.imageTilesDescriptor.tileMargin
        return {
            url,
            remoteTileSize,
            remoteTileMargin,
            tileSize: this.tileSize,
            tileMargin: this.tileMargin,
            remoteMipLevelCorrectionScale,
            tileIndex,
        }
    }

    private readonly _atlasTilesPerRemoteTile: Vector2Like
    private readonly _numTileMipLevels: number
    private readonly _numTiles: Vector2Like
    private readonly _tileIndex: TiledImageIndex
}
